import React, {
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { registerComponent } from "@/lib/vulcan-lib/components";
import { captureException } from "@sentry/core";
import { useTheme } from "@/components/themes/useTheme";
import WrappedShareButton, { WRAPPED_SHARE_BUTTON_WIDTH } from "./WrappedShareButton";
import { useForumWrappedContext } from "./hooks";
import { getWrappedVideo } from "./videos";
import { lightbulbIcon } from "@/components/icons/lightbulbIcon";
import classNames from "classnames";
import WrappedSection from "./WrappedSection";
import WrappedHeading from "./WrappedHeading";

const styles = (theme: ThemeType) => ({
  root: {
    width: "100%",
    minHeight: "100%",
  },
  container: {
    position: "relative",
    width: "100%",
    minHeight: "100%",
    overflow: "hidden",
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px 8px 90px 8px',
  },
  transparent: {},
  grey: {
    background: theme.palette.wrapped.personality.grey,
  },
  red: {
    background: theme.palette.wrapped.personality.red,
  },
  blue: {
    background: theme.palette.wrapped.personality.blue,
  },
  green: {
    background: theme.palette.wrapped.personality.green,
  },
  videoContainer: {
    overflow: "hidden",
  },
  video: {
    maxWidth: "100%",
    height: "100%",
    margin: "0 auto",
  },
  videoThinking: {
    marginTop: -80,
  },
  content: {
    width: "100%",
  },
  personalityText: {
    fontSize: 38,
    marginBottom: 8,
  },
  bottomMargin: {
    marginBottom: 8,
  },
  share: {
    position: "fixed",
    bottom: 72,
    left: `calc(50% - ${WRAPPED_SHARE_BUTTON_WIDTH / 2}px)`,
    transition: "opacity 0.5s ease-in-out",
  },
  shareHidden: {
    opacity: 0,
    pointerEvents: "none",
  },
  screenshotContainer: {
    position: "absolute",
    top: -10000,
    left: -10000,
    width: 500,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "40px 20px 90px",
  },
  screenshotImage: {
    width: "100%",
    marginTop: 8,
  },
  fallbackImage: {
    width: "100%",
    height: "100%",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "contain",
    },
  },
  shareHeading: {
    margin: "24px 0",
    fontSize: 20,
  },
  footer: {
    display: "flex",
    flexDirection: "row",
    gap: "4px",
    alignItems: "center",
    justifyContent: "center",
    textTransform: "uppercase",
    fontSize: 16,
    fontWeight: 400,
    lineHeight: "110%",
    letterSpacing: "0.39px",
  },
  footerLightbulb: {
    width: 22,
  },
});

const WrappedPersonalitySection = ({classes}: {
  classes: ClassesType<typeof styles>,
}) => {
  const {
    year,
    data: {personality},
    thinkingVideoRef,
    personalityVideoRef,
  } = useForumWrappedContext();
  const [video, setVideo] = useState(() => getWrappedVideo("thinking"));
  const [isFinished, setIsFinished] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const videoDisplayRef = useRef<HTMLVideoElement>(null);
  const screenshotRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const isThinking = video.animation === "Intro";
  const videoRef = isThinking ? thinkingVideoRef : personalityVideoRef;

  useEffect(() => {
    setTimeout(() => {
      setIsFinished(true);
    }, 7000);
  }, []);

  useEffect(() => {
    const displayElem = videoDisplayRef.current;
    if (displayElem) {
      const handler = () => {
        if (isThinking) {
          setVideo(getWrappedVideo(personality));
        }
      }
      displayElem.addEventListener("ended", handler);
      return () => displayElem.removeEventListener("ended", handler);
    }
  }, [isThinking, personality]);

  const src = videoRef.current?.src;

  // When we change the video source we have to explicitly tell the video to
  // load otherwise it won't play (this is a known bug in mobile safari)
  useEffect(() => {
    const videoDisplayElement = videoDisplayRef.current;
    if (src && videoDisplayElement) {
      void (async () => {
        try {
          videoDisplayElement.load();
          await videoDisplayElement.play();
        } catch (e) {
          const err = new Error("Wrapped video play error", {cause: e});
          captureException(err);
          // eslint-disable-next-line no-console
          console.error(err);
        }
      })();
    }
  }, [src]);

  const onContextMenu = useCallback((ev: MouseEvent<HTMLVideoElement>) => {
    ev.preventDefault();
  }, []);

  const onError = useCallback((e: SyntheticEvent<HTMLVideoElement, Event>) => {
    setHasVideoError(true);
    if (isThinking) {
      setTimeout(() => {
        setVideo(getWrappedVideo(personality));
        setIsFinished(true);
      }, 2000);
    }
    const err = new Error("Wrapped video callback error", {
      cause: e as AnyBecauseHard,
    });
    captureException(err);
    // eslint-disable-next-line no-console
    console.error(err);
  }, [isThinking, personality]);

  const personalityVideo = getWrappedVideo(personality);

  // There's a horrible line of white background at the bottom of the image
  // because of a bug in html2canvas - cover it up
  const onRendered = useCallback((canvas: HTMLCanvasElement) => {
    try {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const coverHeight = 4;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.fillStyle = theme.palette.wrapped.personality[personalityVideo.color];
        ctx.fillRect(0, canvas.height - coverHeight, canvas.width, coverHeight * 2);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }, [personalityVideo.color, theme]);

  return (
    <WrappedSection
      pageSectionContext="personality"
      className={classNames(classes.root, classes[video.color])}
      noPadding
    >
      <div className={classNames(classes.container, classes[video.color])}>
        <div className={classes.content}>
          {isThinking &&
            <WrappedHeading>
              Your EA Forum personality {year} is...
            </WrappedHeading>
          }
          {!isThinking &&
            <>
              <div className={classes.bottomMargin}>
                Your EA Forum personality {year} is
              </div>
              <WrappedHeading className={classes.personalityText}>
                {personality}
              </WrappedHeading>
            </>
          }
        </div>
        <div className={classes.videoContainer}>
          {hasVideoError
            ? (
              <div className={classes.fallbackImage}>
                <img src={video.frame} alt={personality} crossOrigin="anonymous" />
              </div>
            )
            : (
              <video
                ref={videoDisplayRef}
                src={src}
                loop={!isThinking}
                onContextMenu={onContextMenu}
                onError={onError}
                muted
                playsInline
                autoPlay
                preload="auto"
                className={classNames(
                  classes.video,
                  isThinking && classes.videoThinking,
                )}
              />
            )
          }
        </div>
      </div>
      <WrappedShareButton
        name="Personality"
        screenshotRef={screenshotRef}
        onRendered={onRendered}
        className={classNames(classes.share, !isFinished && classes.shareHidden)}
      />
      <div
        ref={screenshotRef}
        className={classNames(classes.screenshotContainer, classes[video.color])}
      >
        <div className={classes.shareHeading}>
          My EA Forum personality {year} is
        </div>
        <WrappedHeading className={classes.personalityText}>
          {personality}
        </WrappedHeading>
        <img
          src={personalityVideo.frame}
          crossOrigin="anonymous"
          className={classes.screenshotImage}
        />
        <div className={classes.footer}>
          <div className={classes.footerLightbulb}>{lightbulbIcon}</div>
          <div>forum.effectivealtruism.org/wrapped</div>
        </div>
      </div>
    </WrappedSection>
  );
}

export default registerComponent(
  "WrappedPersonalitySection",
  WrappedPersonalitySection,
  {styles},
);
